import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import GoalContext from './GoalContext';
import AuthContext from './AuthContext';
import ApiService from '../services/api';

const GoalProvider = ({ children }) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token, isAuthenticated } = useContext(AuthContext);

  // Fetch all goals for the logged-in user
  const fetchGoals = async () => {
    if (!isAuthenticated || !token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.get('/api/goals', {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGoals(data.goalList);
      } else {
        setError(data.message || 'Failed to fetch goals');
        toast.error(data.message || 'Failed to fetch goals');
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
      setError('Failed to connect to server');
      toast.error('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Add a new goal
  const addGoal = async (goalData) => {
    if (!isAuthenticated || !token) return null;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await ApiService.post('/api/goals', goalData, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGoals([...goals, data.goal || data.goalList]);
        toast.success('Goal created successfully');
        return data.goal || data.goalList;
      } else {
        setError(data.message || 'Failed to create goal');
        toast.error(data.message || 'Failed to create goal');
        return null;
      }
    } catch (error) {
      console.error('Error creating goal:', error);
      setError('Failed to connect to server');
      toast.error('Failed to connect to server');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing goal
  const updateGoal = async (goalId, goalData) => {
    if (!isAuthenticated || !token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await ApiService.put(`/api/goals/${goalId}`, goalData, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGoals(goals.map(goal => 
          goal._id === goalId ? data.goal || data.data : goal
        ));
        toast.success('Goal updated successfully');
        return data.goal || data.goalList;
      } else {
        setError(data.message || 'Failed to update goal');
        toast.error(data.message || 'Failed to update goal');
        return null;
      }
    } catch (error) {
      console.error('Error updating goal:', error);
      setError('Failed to connect to server');
      toast.error('Failed to connect to server');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update goal progress
  const updateGoalProgress = async (goalId, progressData) => {
    if (!isAuthenticated || !token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await ApiService.post(`/api/goals/${goalId}/progress`, progressData, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGoals(goals.map(goal => 
          goal._id === goalId ? data.goal || data.data : goal
        ));
        toast.success('Goal progress updated');
        return data.goal || data.goalList;
      } else {
        setError(data.message || 'Failed to update goal progress');
        toast.error(data.message || 'Failed to update goal progress');
        return null;
      }
    } catch (error) {
      console.error('Error updating goal progress:', error);
      setError('Failed to connect to server');
      toast.error('Failed to connect to server');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Delete a goal
  const deleteGoal = async (goalId) => {
    if (!isAuthenticated || !token) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await ApiService.delete(`/api/goals/${goalId}`, {
        'Authorization': `Bearer ${token}`
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGoals(goals.filter(goal => goal._id !== goalId));
        toast.success('Goal deleted successfully');
        return true;
      } else {
        setError(data.message || 'Failed to delete goal');
        toast.error(data.message || 'Failed to delete goal');
        return false;
      }
    } catch (error) {
      console.error('Error deleting goal:', error);
      setError('Failed to connect to server');
      toast.error('Failed to connect to server');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Fetch goals when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchGoals();
    } else {
      setGoals([]);
    }
  }, [isAuthenticated]);

  return (
    <GoalContext.Provider
      value={{
        goals,
        loading,
        error,
        fetchGoals,
        addGoal,
        updateGoal,
        updateGoalProgress,
        deleteGoal
      }}
    >
      {children}
    </GoalContext.Provider>
  );
};

export default GoalProvider;
